import { startCase, isNil } from 'lodash';
import { FinanceApplicationStatus } from '@upstart/harmony/lib/legos/constants';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Loader } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { getUserMessage } from '@upstart/harmony/lib/error-utils';
import { fmtPrice, fmtPercent, fmtDate } from '@upstart/harmony/lib/formatter';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { BiError } from 'react-icons/bi';
import './LoanStatusList.scss';
import ErrorMessage from '../ErrorMessage';

const FINANCE_APPLICATION_ERROR_STATUS = 'Error Found';

export const getStatus = (item, title) => {
  if (item.status === FinanceApplicationStatus.FAILED) {
    return FINANCE_APPLICATION_ERROR_STATUS;
  }

  if (!title) return '-';
  switch (title) {
    case 'Approved - Conditional':
      return 'Approved';
    default:
      return startCase(title);
  }
};

export const getErrorMessageIfExists = (financeApplication) =>
  financeApplication?.response?.errors?.[0] || null;

export const renderErrorToolTip = (id, message) => (
  <OverlayTrigger
    placement="bottom"
    delayHide={300}
    overlay={
      <Tooltip color="red" id={id}>
        {message}
      </Tooltip>
    }
  >
    <BiError size={20} className="loan-status-error-message" />
  </OverlayTrigger>
);

const FileName = {
  retail_installment_contract: 'RIC',
};

const hasDownloadURLAndFileName = (item) =>
  item && item.url && item.type && FileName[item.type];

const generateTableData = (loanStatusList) => {
  const tableData = [];

  for (const loanStatus of loanStatusList) {
    const { financeApplication, status } = loanStatus;
    const { dealId, car, deal, dealer, financeResponses, createdAt, user } =
      financeApplication;

    const contractNumber = status?.contractNumber || '-';
    const dealerName = dealer?.name || '-';

    let customer = '-';
    const { applicant } = deal;
    if (applicant && applicant.firstName && applicant.lastName) {
      customer = `${applicant.firstName} ${applicant.lastName}`;
    }

    const salesperson = user ? `${user?.firstName} ${user?.lastName}` : '-';
    const vehicle = car.name || '-';

    let responseId;
    let amount;
    let rate;
    let term;
    let payment;
    const financeResponse = financeResponses?.[0];
    if (financeResponse) {
      responseId = financeResponse.id;
      amount = !isNil(financeResponse.data.financeAmount)
        ? fmtPrice(financeResponse.data.financeAmount)
        : '-';
      rate = !isNil(financeResponse.data.apr)
        ? fmtPercent(financeResponse.data.apr)
        : '-';
      term = !isNil(financeResponse.data.months)
        ? `${financeResponse.data.months} Mos`
        : '-';
      payment = !isNil(financeResponse.data.monthly)
        ? fmtPrice(financeResponse.data.monthly)
        : '-';
    }

    const financeStatus = getStatus(financeApplication, status.title || '');
    const errorMessage = getErrorMessageIfExists(financeApplication);
    const date = fmtDate(createdAt) || '-';
    const actionData = {
      dealId,
      downloadUrls: status.downloadUrls,
      responseId,
    };

    tableData.push({
      contractNumber,
      dealer: dealerName,
      customer,
      salesperson,
      vehicle,
      amount,
      rate,
      term,
      payment,
      status: financeStatus,
      errorMessage,
      date,
      actionData,
    });
  }

  return tableData;
};

const tableOptions = {
  defaultSortName: 'date',
  defaultSortOrder: 'desc',
};

const actionFormatter = (cellData, isAllLoanStatus) => {
  const { dealId, downloadUrls, responseId } = cellData;

  return (
    <Dropdown className="loan-action" id="loan-action">
      <Dropdown.Toggle>
        <i className="glyphicon glyphicon-option-vertical" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {dealId && (
          <Link target="_blank" to={{ pathname: `/pencil/${dealId}` }}>
            View Deal
          </Link>
        )}
        {!isAllLoanStatus &&
          (downloadUrls || [])
            .filter(hasDownloadURLAndFileName)
            .map((downloadItem) => (
              <Link
                target="_blank"
                key={downloadItem.type}
                to={{ pathname: downloadItem.url }}
              >
                Download {FileName[downloadItem.type]}
              </Link>
            ))}
        {!isAllLoanStatus && dealId && responseId && (
          <Link
            target="_blank"
            to={{ pathname: `/document-upload/${dealId}/${responseId}` }}
          >
            Upload Documents
          </Link>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const statusFormatter = (cellData, row, _formatExtraData, rowIndex) => {
  if (cellData === FINANCE_APPLICATION_ERROR_STATUS) {
    const errorMessage = row.errorMessage;
    return (
      <>
        <span className="loan-status-error">
          {FINANCE_APPLICATION_ERROR_STATUS}
        </span>
        {!!errorMessage &&
          renderErrorToolTip(
            `errorMessage-${rowIndex}`,
            `Credit Application Error: ${getUserMessage(errorMessage)}`,
          )}
      </>
    );
  }
  return cellData;
};

export default function LoanStatusList({
  loanStatusList,
  error,
  isFetching,
  isAllLoanStatus,
}) {
  const tableData = useMemo(
    () => generateTableData(loanStatusList),
    [loanStatusList],
  );

  if (error) {
    error.error = 'Error fetching application status. Please try again later.';
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="loan-status-list-container">
      <div className="loan-status-list-table-container">
        <BootstrapTable
          data={tableData}
          pagination
          hover
          options={tableOptions}
          bordered={false}
          containerClass="loan-status-list-table"
          tableHeaderClass="table-header"
          bodyContainerClass="body-container"
        >
          <TableHeaderColumn dataField="contractNumber" isKey dataSort>
            Contract Number
          </TableHeaderColumn>
          {isAllLoanStatus && (
            <TableHeaderColumn dataField="dealer" dataSort>
              Dealer
            </TableHeaderColumn>
          )}
          <TableHeaderColumn dataField="customer" dataSort>
            Customer
          </TableHeaderColumn>
          <TableHeaderColumn dataField="salesperson" dataSort>
            Salesperson
          </TableHeaderColumn>
          <TableHeaderColumn dataField="vehicle" dataSort>
            Vehicle
          </TableHeaderColumn>
          <TableHeaderColumn dataField="amount" dataSort>
            Amount
          </TableHeaderColumn>
          <TableHeaderColumn dataField="rate" dataSort>
            Rate
          </TableHeaderColumn>
          <TableHeaderColumn dataField="term" dataSort>
            Term
          </TableHeaderColumn>
          <TableHeaderColumn dataField="payment" dataSort>
            Payment
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="status"
            dataSort
            dataFormat={statusFormatter}
          >
            Status
          </TableHeaderColumn>
          <TableHeaderColumn dataField="date" dataSort>
            Date
          </TableHeaderColumn>
          <TableHeaderColumn
            dataField="actionData"
            dataFormat={(cell) => actionFormatter(cell, isAllLoanStatus)}
          />
        </BootstrapTable>

        {isFetching && <Loader active inline="centered" />}
      </div>
    </div>
  );
}

LoanStatusList.propTypes = {
  loanStatusList: PropTypes.array,
  error: PropTypes.any,
  isFetching: PropTypes.any,
  isAllLoanStatus: PropTypes.bool,
};
